<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { REPORTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useAlert, useTrack } from 'dashboard/composables';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import FilterSelector from './components/FilterSelector.vue';
import ReportLoadingContent from './components/ReportLoadingContent.vue';
import ReportDownloadMenu from './components/ReportDownloadMenu.vue';

export default {
  name: 'AgentsInboxesReports',
  components: {
    FilterSelector,
    ReportLoadingContent,
    UserAvatarWithName,
    ReportDownloadMenu,
  },
  data() {
    return {
      from: null,
      to: null,
      conversationDirection: 'all',
      comunicanteCampaign: 'all',
      selectedAgents: [],
      selectedInboxes: [],
      hasHorizontalOverflow: false,
      isScrolledToEnd: false,
      isExportingXlsx: false,
    };
  },
  computed: {
    ...mapGetters({
      report: 'getAgentsInboxesReport',
      reportUiFlags: 'getAgentsInboxesReportUIFlags',
    }),
    inboxes() {
      return this.report.inboxes || [];
    },
    rows() {
      return this.report.rows || [];
    },
    hasRows() {
      return this.rows.length > 0 && this.inboxes.length > 0;
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        conversationDirection: this.conversationDirection,
        comunicanteCampaign: this.comunicanteCampaign,
        agentIds: this.selectedAgents.map(agent => agent.id),
        inboxIds: this.selectedInboxes.map(inbox => inbox.id),
      };
    },
    isFetching() {
      return this.reportUiFlags.isFetching;
    },
    isDownloadDisabled() {
      return this.isFetching || this.isExportingXlsx || !this.from || !this.to;
    },
    columnTotals() {
      return this.inboxes.reduce((result, inbox) => {
        result[inbox.id] = this.rows.reduce(
          (sum, row) => sum + this.cellValue(row, inbox.id),
          0
        );
        return result;
      }, {});
    },
    grandTotal() {
      return this.rows.reduce((sum, row) => sum + (row.total || 0), 0);
    },
    topAgents() {
      return [...this.rows]
        .sort((a, b) => (b.total || 0) - (a.total || 0))
        .slice(0, 6);
    },
    topInboxes() {
      return this.inboxes
        .map(inbox => ({ ...inbox, total: this.columnTotals[inbox.id] || 0 }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 6);
    },
    maxCellValue() {
      return Math.max(
        ...this.rows.flatMap(row =>
          this.inboxes.map(inbox => this.cellValue(row, inbox.id))
        ),
        1
      );
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateHorizontalScrollState);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateHorizontalScrollState);
  },
  methods: {
    loadReports() {
      if (!this.from || !this.to) {
        return null;
      }

      return this.$store
        .dispatch('fetchAgentsInboxesReport', this.requestPayload)
        .then(() => {
          this.$nextTick(this.updateHorizontalScrollState);
        })
        .catch(() => {
          useAlert(this.$t('AGENTS_INBOXES_REPORTS.DATA_FETCHING_FAILED'));
        });
    },
    async downloadTableXlsx() {
      if (this.isDownloadDisabled) {
        return null;
      }

      this.isExportingXlsx = true;
      try {
        return await this.$store.dispatch('downloadEntityReportXlsx', {
          type: 'agents_inboxes',
          ...this.requestPayload,
          fileName: 'agentes-por-caixa-de-entrada.xlsx',
        });
      } finally {
        this.isExportingXlsx = false;
      }
    },
    onFilterChange({
      from,
      to,
      selectedComunicanteCampaign,
      selectedConversationDirection,
      selectedAgents,
      selectedInbox,
    }) {
      this.from = from;
      this.to = to;
      this.conversationDirection = selectedConversationDirection?.id || 'all';
      this.comunicanteCampaign = selectedComunicanteCampaign?.id || 'all';
      this.selectedAgents = selectedAgents || [];
      this.selectedInboxes = Array.isArray(selectedInbox) ? selectedInbox : [];
      this.loadReports();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from,
          to,
          conversationDirection: this.conversationDirection,
          comunicanteCampaign: this.comunicanteCampaign,
          agentIds: this.requestPayload.agentIds,
          inboxIds: this.requestPayload.inboxIds,
        },
        reportType: 'agents_inboxes',
      });
    },
    cellValue(row, inboxId) {
      return row.inboxes?.[inboxId] ?? row.inboxes?.[String(inboxId)] ?? 0;
    },
    cellIntensityClass(value) {
      if (!value) return 'bg-slate-25 dark:bg-slate-900/40';
      const ratio = value / this.maxCellValue;
      if (ratio >= 0.75) return 'bg-woot-600 text-white';
      if (ratio >= 0.5) return 'bg-woot-500/70 text-white';
      if (ratio >= 0.25) return 'bg-woot-400/40';
      return 'bg-woot-300/20';
    },
    agentCellUser(agent) {
      return {
        name: agent.name,
        thumbnail: agent.thumbnail,
        email: agent.email,
      };
    },
    updateHorizontalScrollState() {
      const tableScroller = this.$refs.tableScroller;
      if (!tableScroller) {
        return;
      }

      this.hasHorizontalOverflow =
        tableScroller.scrollWidth > tableScroller.clientWidth;
      this.isScrolledToEnd =
        tableScroller.scrollLeft + tableScroller.clientWidth >=
        tableScroller.scrollWidth - 1;
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <div class="flex flex-col gap-3 md:flex-row md:items-start">
      <div class="flex-1">
        <FilterSelector
          show-agents-filter
          show-inbox-filter
          multiple-inbox-filter
          :show-business-hours-switch="false"
          @filter-change="onFilterChange"
        />
      </div>
      <ReportDownloadMenu
        :disabled="isDownloadDisabled"
        :loading="isExportingXlsx"
        :xlsx-handler="downloadTableXlsx"
      />
    </div>

    <!-- prettier-ignore -->
    <div>
    <div v-if="hasRows" class="grid grid-cols-1 gap-4 mb-4 xl:grid-cols-2">
      <section
        class="p-5 bg-white border border-solid rounded-md dark:bg-slate-800 border-slate-75 dark:border-slate-700"
      >
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Total de conversas por agente
        </h3>
        <div class="space-y-3">
          <div
            v-for="row in topAgents"
            :key="`agent-summary-${row.agent.id}`"
            class="grid grid-cols-[1fr_1fr_2rem] items-center gap-3 text-sm"
          >
            <span class="truncate text-slate-700 dark:text-slate-200">{{
              row.agent.name
            }}</span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full rounded-full bg-violet-500"
                :style="{
                  width: `${((row.total || 0) / Math.max(grandTotal, 1)) * 100}%`,
                }"
              />
            </span>
            <strong class="text-right text-slate-800 dark:text-slate-100">{{
              row.total || 0
            }}</strong>
          </div>
        </div>
      </section>

      <section
        class="p-5 bg-white border border-solid rounded-md dark:bg-slate-800 border-slate-75 dark:border-slate-700"
      >
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Total de conversas por caixa de entrada
        </h3>
        <div class="space-y-3">
          <div
            v-for="inbox in topInboxes"
            :key="`inbox-summary-${inbox.id}`"
            class="grid grid-cols-[1fr_1fr_2rem] items-center gap-3 text-sm"
          >
            <span class="truncate text-slate-700 dark:text-slate-200">{{
              inbox.name
            }}</span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full rounded-full bg-woot-500"
                :style="{
                  width: `${(inbox.total / Math.max(grandTotal, 1)) * 100}%`,
                }"
              />
            </span>
            <strong class="text-right text-slate-800 dark:text-slate-100">{{
              inbox.total
            }}</strong>
          </div>
        </div>
      </section>
    </div>

    <section
      class="overflow-hidden bg-white border rounded-md dark:bg-slate-800 border-slate-100 dark:border-slate-700"
    >
      <div
        class="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700"
      >
        <h3 class="mb-0 text-lg font-medium text-slate-800 dark:text-slate-100">
          {{ $t('AGENTS_INBOXES_REPORTS.TABLE.HEADER') }}
        </h3>
      </div>

      <ReportLoadingContent
        v-slot="{ visibleLoading }"
        :is-loading="isFetching"
      >
        <woot-loading-state
          v-if="visibleLoading"
          class="px-4 py-12 text-xs"
          :message="$t('AGENTS_INBOXES_REPORTS.LOADING')"
        />

        <div
          v-else-if="!hasRows"
          class="px-4 py-12 text-sm text-center text-slate-600 dark:text-slate-300"
        >
          {{ $t('AGENTS_INBOXES_REPORTS.NO_RECORDS') }}
        </div>

        <template v-else>
          <div
            v-if="hasHorizontalOverflow && !isScrolledToEnd"
            class="flex items-center justify-end gap-1 px-4 pt-3 text-xs text-slate-500 dark:text-slate-400"
          >
            <span>{{ $t('AGENTS_INBOXES_REPORTS.SCROLL_HINT') }}</span>
            <fluent-icon icon="chevron-right" :size="14" />
          </div>

          <div
            v-else-if="hasHorizontalOverflow"
            class="flex items-center justify-start gap-1 px-4 pt-3 text-xs text-slate-500 dark:text-slate-400"
          >
            <fluent-icon icon="chevron-left" :size="14" />
            <span>{{ $t('AGENTS_INBOXES_REPORTS.SCROLL_HINT_BACK') }}</span>
          </div>

          <div
            ref="tableScroller"
            class="overflow-x-auto"
            @scroll="updateHorizontalScrollState"
          >
            <table class="min-w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th
                    class="sticky left-0 z-10 min-w-[220px] bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                  >
                    {{ $t('AGENTS_INBOXES_REPORTS.TABLE.AGENT') }}
                  </th>
                  <th
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :title="inbox.name"
                    class="min-w-[140px] max-w-[220px] bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                  >
                    <span class="block truncate">{{ inbox.name }}</span>
                  </th>
                  <th
                    class="min-w-[120px] bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                  >
                    {{ $t('AGENTS_INBOXES_REPORTS.TABLE.TOTAL') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in rows"
                  :key="row.agent.id"
                  class="border-t border-slate-100 dark:border-slate-700"
                >
                  <td
                    class="sticky left-0 z-10 border-t bg-white px-4 py-3 text-left align-middle dark:bg-slate-800 border-slate-100 dark:border-slate-700"
                  >
                    <UserAvatarWithName
                      :user="agentCellUser(row.agent)"
                      size="22px"
                      text-class="text-sm font-medium text-slate-800"
                      :show-link="false"
                    />
                  </td>
                  <td
                    v-for="inbox in inboxes"
                    :key="`${row.agent.id}-${inbox.id}`"
                    class="border-t px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                    :class="cellIntensityClass(cellValue(row, inbox.id))"
                  >
                    {{ cellValue(row, inbox.id) }}
                  </td>
                  <td
                    class="border-t px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100 border-slate-100 dark:border-slate-700"
                  >
                    {{ row.total }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td
                    class="sticky left-0 z-10 border-t bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300 border-slate-100 dark:border-slate-700"
                  >
                    {{ $t('AGENTS_INBOXES_REPORTS.TABLE.TOTAL') }}
                  </td>
                  <td
                    v-for="inbox in inboxes"
                    :key="`total-${inbox.id}`"
                    class="border-t bg-slate-50 px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-700 dark:bg-slate-900 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                  >
                    {{ columnTotals[inbox.id] }}
                  </td>
                  <td
                    class="border-t bg-slate-50 px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-800 dark:bg-slate-900 dark:text-slate-100 border-slate-100 dark:border-slate-700"
                  >
                    {{ grandTotal }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </template>
      </ReportLoadingContent>
    </section>
    </div>
  </div>
</template>
